import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/utils/analytics';
import { Checkbox } from '@/components/ui/checkbox';
import { useFormAutosave } from '@/hooks/useFormAutosave';
import { reportFailedSubmission } from '@/hooks/useFormFailureBeacon';
import { RestoreDraftBanner } from '@/components/shared/RestoreDraftBanner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ROIPlaybookModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    industryType: '',
    email: '',
    location: '',
    subscribeEmail: false,
    subscribePromos: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    hasDraft, draftAge, restoreDraft, clearDraft, dismissDraftBanner,
  } = useFormAutosave('roi-playbook', formData, setFormData, { enabled: !isSuccess });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const res = await fetch(`/api/roi-playbook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          industry: formData.industryType,
          company: formData.location,
        }),
      });
      if (!res.ok) throw new Error('Submit failed');
      trackEvent('roi_playbook_downloaded', {
        form_type: 'roi_playbook',
        industry: formData.industryType || 'unspecified',
      });
      clearDraft();
      setIsSuccess(true);
    } catch (err) {
      console.error('ROI Playbook submission error:', err);
      reportFailedSubmission({
        formType: 'roi_playbook',
        draft: formData,
        errorReason: err?.message || 'fetch_failed',
      });
      setErrors(prev => ({ ...prev, submit: "We couldn't process your request. Please try again, or email sales@porticosystems.com to request the playbook." }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      industryType: '',
      email: '',
      location: '',
      subscribeEmail: false,
      subscribePromos: false
    });
    setErrors({});
    setIsSuccess(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        {/* Header */}
        <div className="bg-primary text-primary-foreground px-6 py-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              Download Your Free ROI Playbook
            </DialogTitle>
            <p className="text-sm opacity-90 mt-1">
              Get actionable insights to maximize your flooring investment
            </p>
          </DialogHeader>
        </div>

        {isSuccess ? (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
            <h3 className="font-heading font-semibold text-xl mb-2">
              Thank You!
            </h3>
            <p className="text-muted-foreground mb-6">
              Your ROI Playbook is ready! Click below to download your free copy.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/Assets/pdf/Portico_PlayBook_Retail-Flooring.pdf';
                  link.download = 'Portico_Retail-Flooring-ROI-Playbook.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="w-full"
                data-testid="roi-download-btn"
              >
                Download PDF Now
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          </div>
        ) : (
          /* Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <RestoreDraftBanner
              show={hasDraft}
              draftAge={draftAge}
              onRestore={restoreDraft}
              onDismiss={dismissDraftBanner}
              testId="roi-restore-draft"
            />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  placeholder="John"
                  className={errors.firstName ? 'border-destructive' : ''}
                />
                {errors.firstName && (
                  <p className="text-xs text-destructive">{errors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  placeholder="Doe"
                  className={errors.lastName ? 'border-destructive' : ''}
                />
                {errors.lastName && (
                  <p className="text-xs text-destructive">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="industryType">Industry Type</Label>
              <Input
                id="industryType"
                value={formData.industryType}
                onChange={(e) => handleChange('industryType', e.target.value)}
                placeholder="e.g., Retail, Healthcare, Hospitality"
                data-testid="roi-industry-type-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@company.com"
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                placeholder="City, State"
              />
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="subscribeEmail"
                  checked={formData.subscribeEmail}
                  onCheckedChange={(checked) => handleChange('subscribeEmail', checked)}
                />
                <Label htmlFor="subscribeEmail" className="text-sm font-normal cursor-pointer">
                  I'd like to receive educational content and industry insights via email
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="subscribePromos"
                  checked={formData.subscribePromos}
                  onCheckedChange={(checked) => handleChange('subscribePromos', checked)}
                />
                <Label htmlFor="subscribePromos" className="text-sm font-normal cursor-pointer">
                  I'd like to receive promotional offers and product updates
                </Label>
              </div>
            </div>

            <div className="pt-4">
              {errors.submit && (
                <p className="text-sm text-red-500 bg-red-50 p-3 rounded-lg mb-3" data-testid="roi-submit-error">{errors.submit}</p>
              )}
              <Button 
                type="submit" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Download Playbook'}
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              By submitting this form, you agree to our{' '}
              <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ROIPlaybookModal;
